import { COMMA, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import {
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    inject
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import {
    ButtonColor,
    ButtonSize,
    CellCheckboxComponent,
    ChipSelectOption,
    InlineMessageBoxType,
    Options,
    SearchBoxSize,
    SelectSize,
    TooltipPosition
} from '@predictiveindex/tapestry';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { catchError, from, of, Subject, takeUntil, tap } from 'rxjs';
import {
    LanguageAvailabilityByProduct,
    RedirectURLMaxLength,
    SurveyUserTypes
} from '../../../../constants-types';
import {
    FilterInfo,
    FilterType,
    Folder,
    OpenInvitation,
    OpenInvitationLinkType,
    OpenInvitationNotificationType,
    OpenInvitationUpdate,
    StakeholderNotificationInfo,
    User
} from '../../../../core-data-model';
import {
    FoldersDataService,
    SharingUserApiDefault,
    UserApi
} from '../../../../core-services';
import { StringUtilities } from '../../../../core-utilities';
import { Language } from '../../../../shared';
import { InitDataService, Logger } from '../../../../system-services';
import { TranslationCoreService } from '../../../../translations';
import { NotOnlyWhitespace } from '../../../Directives';
import { OILinkNotificationTableValues } from './components/oi-notifications-table/oi-notifications-table.component';
import { IOpenInvitationLink } from './IOpenInvitationLink.interface';
import { OIActionMenuRendererComponent } from './oi-action-menu-renderer.component';
import { OIOpenFolderRendererComponent } from './oi-open-folder-renderer.component';
import { PlatformOpenInvitationService } from './openinvitation.api';
import {
    maxStakeholders,
    minLinkOwner,
    maxLinkOwner,
    validateEmails
} from './validators/oi-form-validators';

@Component({
    selector: 'platform-open-invitation',
    templateUrl: './open-invitation.component.html',
    styleUrls: [
        './open-invitation.component.less',
        '../seller-tools-base.component.less'
    ],
    standalone: false
})
export class OpenInvitationPeekComponent implements OnInit, OnDestroy {
    @Output()
    closePeek: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('oiGrid') oiGrid: AgGridAngular;

    addNewLink = false;
    isEditingLink = false;
    isSubmitting = false;
    currentEditLink: IOpenInvitationLink;
    buttonColor = ButtonColor;
    oiDataSource: IOpenInvitationLink[] = [];
    components = [CellCheckboxComponent, OIActionMenuRendererComponent];
    languageOptions: Options[] = [];
    selectedLanguage: string;
    redirectURL: string;
    linkOwnerOptions: ChipSelectOption[] = [];
    stakeholderOptions: string[] = [];
    loadingStakeholders = false;
    loadingLinkOwner = true;
    searchText: string = '';
    private cachedFallbackAdmin: ChipSelectOption | null = null;
    uniqueUrlErrors = {
        isNotUnique: 'oilink.uniqueLinkURL.error'
    };

    protected readonly SelectSize = SelectSize;
    protected readonly TooltipPosition = TooltipPosition;
    protected readonly SPACE = SPACE;
    protected readonly COMMA = COMMA;
    protected readonly SEMICOLON = SEMICOLON;
    protected readonly ButtonSize = ButtonSize;
    protected readonly InlineMessageBoxType = InlineMessageBoxType;
    protected readonly SearchBoxSize = SearchBoxSize;

    protected prospectFolder: Folder;

    private gridApi!: GridApi;
    private fb = inject(FormBuilder);
    private oiService = inject(PlatformOpenInvitationService);
    private translationCore = inject(TranslationCoreService);
    private userApi = inject(UserApi);
    private initData = inject(InitDataService);
    private stringUtil = inject(StringUtilities);
    private logger = inject(Logger);
    private router = inject(Router);
    private foldersData = inject(FoldersDataService);
    private readonly sharingUserApi = inject(SharingUserApiDefault);

    columnDefs: ColDef[] = [
        {
            headerName: this.translationCore.key('common.link-folder.name'),
            field: 'name',
            sortable: true,
            suppressMovable: true,
            cellRenderer: OIOpenFolderRendererComponent,
            cellRendererParams: (params: any) => ({
                name: params.data.name,
                folderId: params.data.folderId
            })
        },
        {
            headerName: 'Description',
            field: 'description',
            sortable: false,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            suppressMovable: true,
            headerClass: 'ag-right-aligned-header',
            cellClass: 'ag-right-aligned-cell',
            width: 75,
            cellRenderer: OIActionMenuRendererComponent,
            cellRendererParams: (params: any) => ({
                id: params.data.id,
                url: params.data.url
            })
        }
    ];

    oiForm = this.fb.nonNullable.group({
        name: new FormControl('', [
            Validators.required,
            NotOnlyWhitespace,
            Validators.maxLength(50),
            Validators.pattern('[a-zA-Z0-9._ ]+')
        ]),
        description: new FormControl('', [
            Validators.required,
            NotOnlyWhitespace,
            Validators.maxLength(50)
        ]),
        folder: new FormControl(''),
        language: new FormControl('', Validators.required),
        kiosk: new FormControl(false),
        boomerang: new FormControl(false),
        redirectURL: new FormControl('', [
            Validators.maxLength(RedirectURLMaxLength),
            Validators.pattern(this.stringUtil.urlRegExRequiresHttpOrHttps) // Added as part of PRSP-164
        ]),
        notificationSettings: new FormControl<OILinkNotificationTableValues>({
            notifyAssessmentSender: true,
            notifyAssessmentTaker: false,
            notifyStakeholders: false,
            includeBAReportInLinkSenderEmail: true,
            includeBAReportInAssessmentTakerEmail: false,
            includeBAReportInStakeholderEmail: false
        }),
        linkOwner: new FormControl<ChipSelectOption[]>(
            [],
            [minLinkOwner(1), maxLinkOwner(1)]
        ),
        stakeholders: new FormControl<string[]>(
            [],
            [
                validateEmails(this.initData.companyEmailDomains.allowList),
                maxStakeholders(20)
            ]
        )
    });

    private destroyed$ = new Subject<void>();

    async ngOnInit() {
        this.updateTable();
        this.oiService.editLink
            .pipe(takeUntil(this.destroyed$))
            .subscribe((link) => {
                if (link) {
                    this.currentEditLink = link;
                    this.openEditOiLink(link);
                    if (this.currentEditLink.kiosk) {
                        this.oiForm.controls.redirectURL.disable();
                    }
                }
            });
        this.oiService.reloadTable
            .pipe(takeUntil(this.destroyed$))
            .subscribe((reload) => {
                if (reload) {
                    this.updateTable();
                }
            });
        this.foldersData.getProspectsFolder().subscribe((folder) => {
            this.prospectFolder = folder;
        });
        this.languageOptions = this.translationCore
            .getLanguagesFor(LanguageAvailabilityByProduct.FullSite)
            .map<Options>((language) => ({
                value: language.locale,
                displayValue: language.display,
                isDisabled: false
            }));
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    addNewLinkButtonPressed() {
        this.currentEditLink = null;
        this.resetForm();
        this.oiForm.enable();
        this.addNewLink = !this.addNewLink;
        this.fetchLinkOwners(this.initData.user.email).subscribe(() => {
            this.loadingLinkOwner = false;
            const defaultLinkOwner = this.linkOwnerOptions.find(
                (u) => u.value === this.initData.user.email
            );

            if (defaultLinkOwner) {
                this.oiForm.controls.linkOwner.setValue([defaultLinkOwner]);
            }
        });
        this.setLanguageValue();
    }

    cancel() {
        this.addNewLink = !this.addNewLink;
        this.isEditingLink = false;
        this.resetForm();
    }

    assessRedirectUrl(checkChange: MatCheckboxChange) {
        if (checkChange.checked) {
            this.oiForm.controls.redirectURL.disable();
        } else {
            if (this.currentEditLink) {
                this.oiForm.controls.redirectURL.disable();
            } else {
                this.oiForm.controls.redirectURL.enable();
            }
        }
    }

    setLanguageValue() {
        const languageToSet = this.currentEditLink
            ? this.languageOptions.find(
                  (option) => option.value === this.currentEditLink.language
              )
            : this.languageOptions.find((option) => option.value === 'en-US');

        this.selectedLanguage = languageToSet.value;
        this.oiForm.controls.language.setValue(this.selectedLanguage);
    }

    updateTable() {
        this.oiService.fetchAllOILinks().then((results) => {
            this.oiDataSource = results
                .filter(
                    (item) =>
                        item.linkType === OpenInvitationLinkType.SellerLink
                )
                .sort(
                    (a, b) =>
                        new Date(b.modifiedTime).getTime() -
                        new Date(a.modifiedTime).getTime()
                );
        });

        this.oiService.reloadTable.next(false);
    }

    viewAllAssessments() {
        this.router.navigate([
            '/seller-experience/assessments/all-assessments'
        ]);
        this.closePeek.emit();
    }

    async createOILink() {
        this.isSubmitting = true;

        try {
            const folder = this.isEditingLink
                ? new Folder({ id: this.currentEditLink?.folderId })
                : await this.oiService.createOIFolder(
                      this.oiForm.value.name,
                      this.prospectFolder.id
                  );
            const languageConfig = new Language({
                locale: this.oiForm.controls.language.value
            });
            const newLink = new OpenInvitationUpdate({
                linkName: this.oiForm.value.name.replace(/ /g, ''),
                description: this.oiForm.value.description,
                personType: SurveyUserTypes.Prospect,
                folder: folder,
                language: languageConfig,
                includeBAReportInAssessmentTakerEmail:
                    this.oiForm.value.notificationSettings
                        .includeBAReportInAssessmentTakerEmail,
                originalObject: this.currentEditLink
                    ? new OpenInvitation({
                          linkName: this.currentEditLink.name,
                          id: this.currentEditLink.id
                      })
                    : null,
                notifyAssessmentSender:
                    this.oiForm.value.notificationSettings
                        .notifyAssessmentSender,
                notifyAssessmentTaker:
                    this.oiForm.value.notificationSettings
                        .notifyAssessmentTaker,
                notifyStakeholders:
                    this.oiForm.value.notificationSettings.notifyStakeholders,
                includeBAReportInLinkSenderEmail:
                    this.oiForm.value.notificationSettings
                        .includeBAReportInLinkSenderEmail,
                includeBAReportInStakeholderEmail:
                    this.oiForm.value.notificationSettings
                        .includeBAReportInStakeholderEmail,
                stakeholderEmails:
                    this.oiForm.value.stakeholders?.join(',') || null,
                ownerEmail: this.oiForm.value.linkOwner[0].value,
                ownerFirstName:
                    this.oiForm.value.linkOwner[0].displayName.split(' ')[0],
                ownerLastName:
                    this.oiForm.value.linkOwner[0].displayName.split(' ')[1],
                stakeholderNotifications: this.buildStakeholderInfo()
            });
            if (!this.oiForm.controls.kiosk.value) {
                newLink.redirectUrl =
                    this.oiForm.controls.redirectURL.value ?? null;
            }
            if (
                !this.currentEditLink ||
                !this.oiForm.controls.redirectURL.value
            ) {
                newLink.kioskMode = this.oiForm.controls.kiosk.value ?? false;
            }
            if (this.isEditingLink) {
                await this.oiService.updateOpenInvitation(
                    newLink,
                    this.currentEditLink.id
                );
                this.isEditingLink = false;
                this.oiService.editLink.next(null);
                this.currentEditLink = null;
            } else {
                await this.oiService.createOpenInvitation(newLink);
            }
            this.addNewLinkButtonPressed();
            this.updateTable();
        } catch (e) {
            this.logger.logError('Seller Tools OI Issue', e);
        } finally {
            this.isSubmitting = false;
        }
    }

    buildStakeholderInfo() {
        if (!this.oiForm.value.stakeholders) {
            return [];
        } else {
            const stakeholders: StakeholderNotificationInfo[] = [];
            this.oiForm.value.stakeholders.forEach((s) => {
                stakeholders.push({
                    email: s,
                    isDeleted: false,
                    notificationType: this.setStakeholderNotificationType()
                } as StakeholderNotificationInfo);
            });
            return stakeholders;
        }
    }

    setStakeholderNotificationType() {
        if (this.oiForm.value.notificationSettings.notifyStakeholders) {
            return this.oiForm.value.notificationSettings
                .includeBAReportInStakeholderEmail
                ? OpenInvitationNotificationType.EmailAndBAReport
                : OpenInvitationNotificationType.Email;
        } else {
            return OpenInvitationNotificationType.None;
        }
    }

    openEditOiLink(existingLink: IOpenInvitationLink) {
        this.fetchLinkOwners(existingLink.linkOwner.value).subscribe(() => {
            const foundLinkOwner = this.linkOwnerOptions.find(
                (o) => o.value === existingLink.linkOwner?.value
            );

            if (foundLinkOwner) {
                this.setFormValuesForEdit(existingLink, [foundLinkOwner]);
            } else {
                this.fetchFallbackLinkOwner(existingLink);
            }
        });
    }

    private fetchFallbackLinkOwner(existingLink: IOpenInvitationLink) {
        if (this.cachedFallbackAdmin) {
            this.linkOwnerOptions = [
                this.cachedFallbackAdmin,
                ...this.linkOwnerOptions
            ];
            this.setFormValuesForEdit(existingLink, [this.cachedFallbackAdmin]);
            return;
        }

        this.sharingUserApi
            .fetchOrganizationAdminUsers()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (adminUsers) => {
                    const firstAdmin = adminUsers?.[0];
                    if (firstAdmin) {
                        const adminOption: ChipSelectOption = {
                            displayName:
                                `${firstAdmin.firstName ?? ''} ${firstAdmin.lastName ?? ''}`.trim(),
                            value: firstAdmin.email ?? ''
                        };
                        this.cachedFallbackAdmin = adminOption;
                        this.linkOwnerOptions = [
                            adminOption,
                            ...this.linkOwnerOptions
                        ];
                        this.setFormValuesForEdit(existingLink, [adminOption]);
                    } else {
                        this.setFormValuesForEdit(existingLink, []);
                    }
                },
                error: () => {
                    this.setFormValuesForEdit(existingLink, []);
                }
            });
    }

    private setFormValuesForEdit(
        existingLink: IOpenInvitationLink,
        linkOwnerValue: ChipSelectOption[]
    ) {
        this.oiForm.setValue({
            name: existingLink.name,
            description: existingLink.description,
            language: existingLink.language ?? 'en-US',
            kiosk: existingLink.kiosk,
            boomerang: existingLink.boomerang,
            folder: existingLink.folderId,
            redirectURL: this.currentEditLink?.redirectURL,
            stakeholders: this.currentEditLink?.stakeholders
                ? this.currentEditLink?.stakeholders.split(',')
                : [],
            linkOwner: linkOwnerValue,
            notificationSettings: {
                notifyAssessmentSender:
                    existingLink?.notificationSettings.notifyAssessmentSender ??
                    true,
                notifyAssessmentTaker:
                    existingLink?.notificationSettings.notifyAssessmentTaker ??
                    false,
                notifyStakeholders:
                    existingLink?.notificationSettings.notifyStakeholders ??
                    false,
                includeBAReportInLinkSenderEmail:
                    existingLink?.notificationSettings
                        .includeBAReportInLinkSenderEmail ?? true,
                includeBAReportInAssessmentTakerEmail:
                    existingLink?.notificationSettings
                        .includeBAReportInAssessmentTakerEmail ?? false,
                includeBAReportInStakeholderEmail:
                    existingLink?.notificationSettings
                        .includeBAReportInStakeholderEmail ?? false
            }
        });
        this.setLanguageValue();
        this.isEditingLink = true;
        this.addNewLink = true;
        this.loadingLinkOwner = false;
    }

    linkOwnerFilterChange(filterTerm?: string) {
        if (filterTerm) {
            this.fetchLinkOwners(filterTerm).subscribe(() => {
                this.loadingLinkOwner = false;
            });
        }
    }

    stakeholderFilterChange(filterTerm?: string) {
        if (filterTerm) {
            this.fetchStakeholders(filterTerm);
        }
    }

    fetchLinkOwners(searchTerm?: string) {
        this.loadingLinkOwner = true;

        return this.fetchUsers(searchTerm)
            .pipe(
                catchError((error) => {
                    this.logger.logError('Error fetching users:', error);
                    return of([] as User[]);
                })
            )
            .pipe(
                takeUntil(this.destroyed$),
                tap((users) => {
                    this.linkOwnerOptions = users.map<ChipSelectOption>(
                        (u) => ({
                            displayName: `${u.firstName} ${u.lastName}`.trim(),
                            value: u.email
                        })
                    );
                })
            );
    }

    fetchStakeholders(searchTerm?: string) {
        this.loadingStakeholders = true;

        this.fetchUsers(searchTerm)
            .pipe(
                catchError((error) => {
                    this.logger.logError('Error fetching users:', error);
                    return of([] as User[]);
                })
            )
            .pipe(takeUntil(this.destroyed$))
            .subscribe((users) => {
                this.stakeholderOptions = users.map((u) => u.email);
                this.loadingStakeholders = false;
            });
    }

    private fetchUsers(searchTerm?: string) {
        let filters: FilterInfo[] = null;

        if (searchTerm) {
            filters = [
                new FilterInfo({
                    type: FilterType.Search,
                    parameters: { term: searchTerm.trim().toLowerCase() }
                })
            ];
        }

        return from(this.userApi.getUsers(50, filters, 'all'));
    }

    private resetForm() {
        const defaultLinkOwner = this.linkOwnerOptions.find(
            (u) => u.value === this.initData.user.email
        );

        this.oiForm.reset({
            linkOwner: defaultLinkOwner ? [defaultLinkOwner] : [],
            stakeholders: [],
            notificationSettings: {
                notifyAssessmentTaker: false,
                notifyStakeholders: false,
                notifyAssessmentSender: true,
                includeBAReportInAssessmentTakerEmail: false,
                includeBAReportInStakeholderEmail: false,
                includeBAReportInLinkSenderEmail: true
            },
            language: this.selectedLanguage
        });
        this.oiForm.markAsPristine();
        this.oiForm.markAsUntouched();
        this.oiForm.updateValueAndValidity();
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
    }

    onFilterTextChanged(searchText: string) {
        this.gridApi.setGridOption('quickFilterText', searchText);
    }
}
